import React from 'react';
import { render } from 'react-dom';
/** ############################# Auth ############################### */
import Syndicate from './pages/Auth/utils/Syndicate';
/** ############################# UTILS ############################### */
import whyDidYouRender from '@welldone-software/why-did-you-render';
import { IS_LOCALHOST } from './config/constants';
import './styles/fonts.css';
import ReactRoutes, { routes } from './pages/ReactRoutes';
import AppHocWrapper from './pages/AppHocWrapper';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import GlobalStyles from './styles/globalStyles';
import Cognito from './pages/Auth/utils/Cognito';
import 'moment-timezone';
import { assertNotNil } from './utils/types';
import { colors } from './config/styles';
import ErrorPage from './pages/ErrorPage';
import { FallbackProps, ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/browser';

/* Check for component unnecessary renders only on localhost.
  For more info and how to use: https://github.com/welldone-software/why-did-you-render */
if (IS_LOCALHOST && process.env.NODE_ENV !== 'production') {
  whyDidYouRender(React, {
    trackAllPureComponents: false, // Debug statements
  });
}

// IMPORTANT:
// Even though, App is wrapped with BrowserRouter, we should not use hooks
// useNavigate, useSearchParams, useLocation, etc. from react-router-dom here.
// Long story short - hooks cause re-render, and we want to avoid it.
// Just use old-school window.location and don't hesitate
// One of the issues: https://github.com/remix-run/react-router/issues/7634
const App = () => {
  return <ReactRoutes />;
};

const FallbackComponent = ({ error }: FallbackProps) => {
  console.error('Fallback Error: ', error);
  Sentry.captureException(error);
  return <ErrorPage />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AppHocWrapper>
        <ReactRoutes />
      </AppHocWrapper>
    ),
    errorElement: <ErrorBoundary FallbackComponent={FallbackComponent} />,
    children: routes,
  },
]);

export const run = async () => {
  // not sure why Svilen assigned boolean to string, but I kept it for now
  // Disable loading Fluent UI fonts (Segoe UI) from Microsoft servers
  // eslint-disable-next-line
  // @ts-ignore
  window.FabricConfig = { fontBaseUrl: false };

  // Fetch the customer data BEFORE the App has booted up
  const syndicateData = await Syndicate.initialize();

  const appRootElement = document.getElementById('root');
  assertNotNil(appRootElement);

  if (!syndicateData) {
    // TODO: maybe move syndicate initialization in the React code to avoid this
    // crude way of building an error message
    const error = document.createElement('h1');
    error.innerText =
      'The Kubit app failed to load. Please try reloading the page in a few seconds.';
    error.style.padding = '16px';
    error.style.textAlign = 'center';
    error.style.color = colors.mainError;
    error.style.fontSize = '18px';
    appRootElement.appendChild(error);
    return;
  }

  Cognito.setSyndicateData(syndicateData);
  render(
    <>
      <GlobalStyles />
      <RouterProvider router={router} />
    </>,
    appRootElement,
  );
};

export const visibleForTesting = { App };
